import {
  ForecastMetric,
  PeriodIssue,
  useSiteReadingsValidation,
} from "@inrange/building-manager-api-client";
import { Tooltip } from "@inrange/theme-components";
import { useEffect } from "react";
import { formatNumber3dp } from "../siteSections/utils";

const formatNumber3dpOrBlank = (value: number | undefined) => {
  if (value === undefined) return "-";
  return formatNumber3dp(value);
};

const valuesMatch = (a: number | undefined, b: number | undefined) => {
  if (a === undefined || b === undefined) return false;
  return Math.abs(a - b) < 0.01;
};

const formatPct = (pct: number | null | undefined): string => {
  if (pct === null || pct === undefined) return "-";
  const rounded = Number(pct.toFixed(1));
  if (rounded === 0) return "+0.0%";
  const sign = rounded > 0 ? "+" : "";
  return `${sign}${rounded.toFixed(1)}%`;
};

const formatActual = (value: number): string => {
  return value.toLocaleString("en-GB", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
};

const formatDuration = (startIso: string, endIso: string): string => {
  const start = new Date(startIso);
  const end = new Date(endIso);
  const diffMs = end.getTime() - start.getTime();
  const totalMins = Math.round(diffMs / (60 * 1000));
  const days = Math.floor(totalMins / (24 * 60));
  const hrs = Math.floor((totalMins % (24 * 60)) / 60);
  const mins = totalMins % 60;
  const parts: string[] = [];
  if (days > 0) parts.push(`${days} day${days !== 1 ? "s" : ""}`);
  if (hrs > 0) parts.push(`${hrs}hr${hrs !== 1 ? "s" : ""}`);
  if (mins > 0) parts.push(`${mins}mins`);
  return parts.join(" ") || "0mins";
};

const formatIssueTime = (
  startIso: string,
  endIso: string,
  timeZone?: string
): string => {
  const start = new Date(startIso);
  const end = new Date(endIso);
  const fmtDate = (d: Date) => {
    const opts: Intl.DateTimeFormatOptions = {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      ...(timeZone ? { timeZone } : {}),
    };
    return d.toLocaleString("en-GB", opts);
  };
  return `${fmtDate(start)} — ${fmtDate(end)} (${formatDuration(startIso, endIso)})`;
};

// Component that validates a single reading file for a specific month
const ReadingValidation = ({
  siteId,
  fileGuid,
  month,
  hasBattery,
  siteTimeZone,
  onIcon,
}: {
  siteId: string;
  fileGuid: string;
  month: string;
  hasBattery: boolean;
  siteTimeZone?: string;
  onIcon?: (icon: string | null) => void;
}) => {
  const { fetchReadingsValidation } = useSiteReadingsValidation({
    siteId,
    fileGuid,
    month,
    enabled: !!month && !!fileGuid,
  });

  const validationData = fetchReadingsValidation.data;
  const isLoading = fetchReadingsValidation.isFetching;

  const fileReading = validationData?.fileReading;
  const dbReading = validationData?.dbReading;
  const forecastComparison = validationData?.forecastComparison;
  const zeroExportCheck = validationData?.zeroExportCheck;
  const periodChecks = validationData?.periodChecks;
  const monthInfo = validationData?.monthInfo;

  const genMatch = valuesMatch(fileReading?.generation, dbReading?.generation);
  const impMatch = valuesMatch(fileReading?.gridImport, dbReading?.gridImport);
  const expMatch = valuesMatch(fileReading?.gridExport, dbReading?.gridExport);
  const bmPvMatch = valuesMatch(
    fileReading?.behindMeterPV,
    dbReading?.behindMeterPV
  );
  const bmBatteryMatch = hasBattery
    ? valuesMatch(
        fileReading?.behindMeterBattery,
        dbReading?.behindMeterBattery
      )
    : true;
  const allMatch =
    fileReading && !fileReading.error
      ? genMatch && impMatch && expMatch && bmPvMatch && bmBatteryMatch
      : null;

  useEffect(() => {
    if (!onIcon) return;
    if (isLoading || !validationData) {
      onIcon(null);
    } else if (allMatch === true) {
      onIcon("\u2705");
    } else if (allMatch === false) {
      onIcon("\u274C");
    } else {
      onIcon(null);
    }
  }, [onIcon, isLoading, validationData, allMatch]);

  if (isLoading) {
    return (
      <span
        style={{ fontWeight: "normal", fontStyle: "italic", color: "#666" }}
      >
        {" "}
        (validating...)
      </span>
    );
  }

  if (!validationData) return null;

  // File vs DB comparison
  const renderFileDbComparison = () => {
    if (!fileReading || fileReading.error) {
      if (fileReading?.error) {
        return (
          <div
            style={{ fontSize: "0.85em", color: "#dc3545", marginTop: "4px" }}
          >
            Error: {fileReading.error}
          </div>
        );
      }
      return null;
    }

    const renderField = (
      label: string,
      fileVal: number | undefined,
      dbVal: number | undefined,
      match: boolean
    ) => {
      if (match) {
        return `${label}: ${formatNumber3dpOrBlank(fileVal)}`;
      }
      return `${label}: File: ${formatNumber3dpOrBlank(fileVal)} vs DB: ${formatNumber3dpOrBlank(dbVal)}`;
    };

    return (
      <div style={{ fontSize: "0.85em", color: "#666", marginTop: "4px" }}>
        {allMatch ? (
          <div>
            {renderField(
              "Generation",
              fileReading.generation,
              dbReading?.generation,
              genMatch
            )}
            {", "}
            {renderField(
              "Import",
              fileReading.gridImport,
              dbReading?.gridImport,
              impMatch
            )}
            {", "}
            {renderField(
              "Export",
              fileReading.gridExport,
              dbReading?.gridExport,
              expMatch
            )}
            <br />
            {renderField(
              "Behind Meter PV",
              fileReading.behindMeterPV,
              dbReading?.behindMeterPV,
              bmPvMatch
            )}
            {hasBattery && (
              <>
                {", "}
                {renderField(
                  "Behind Meter Battery",
                  fileReading.behindMeterBattery,
                  dbReading?.behindMeterBattery,
                  bmBatteryMatch
                )}
              </>
            )}
          </div>
        ) : (
          <>
            <div>
              {"• "}
              {renderField(
                "Generation",
                fileReading.generation,
                dbReading?.generation,
                genMatch
              )}
            </div>
            <div>
              {"• "}
              {renderField(
                "Import",
                fileReading.gridImport,
                dbReading?.gridImport,
                impMatch
              )}
            </div>
            <div>
              {"• "}
              {renderField(
                "Export",
                fileReading.gridExport,
                dbReading?.gridExport,
                expMatch
              )}
            </div>
            <div>
              {"• "}
              {renderField(
                "Behind Meter PV",
                fileReading.behindMeterPV,
                dbReading?.behindMeterPV,
                bmPvMatch
              )}
            </div>
            {hasBattery && (
              <div>
                {"• "}
                {renderField(
                  "Behind Meter Battery",
                  fileReading.behindMeterBattery,
                  dbReading?.behindMeterBattery,
                  bmBatteryMatch
                )}
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  // Forecast comparison rendering
  const renderForecastLine = (
    label: string,
    metric: ForecastMetric | undefined,
    options?: { isDemand?: boolean; unit?: string }
  ) => {
    if (!metric || metric.actual === null) return null;

    const { isDemand = false, unit = "kWh" } = options ?? {};

    const RYG_STYLES: Record<
      string,
      { icon: string; color: string; fontWeight: "bold" | "normal" }
    > = {
      red: { icon: "\uD83D\uDD34", color: "#dc3545", fontWeight: "bold" },
      amber: { icon: "\uD83D\uDFE0", color: "#b8860b", fontWeight: "normal" },
      green: { icon: "\uD83D\uDFE2", color: "#1a7d2f", fontWeight: "normal" },
      pending: {
        icon: "\uD83D\uDD35",
        color: "#0d6efd",
        fontWeight: "normal",
      },
    };

    const style = RYG_STYLES[metric.ryg] ?? RYG_STYLES.green;
    const { icon, color, fontWeight } = style;

    // Curtailment-pending generation: show blue info instead of comparison
    if (metric.ryg === "pending") {
      const formattedActual = formatActual(metric.actual);
      return (
        <div style={{ color, fontWeight }}>
          {icon} {label}: {formattedActual} {unit} (comparison pending
          curtailment data)
        </div>
      );
    }

    const formattedActual = formatActual(metric.actual);
    const isCurrency = unit === "£" || unit === "€" || unit === "$";
    const valueDisplay = isCurrency
      ? `${unit}${formattedActual}`
      : `${formattedActual} ${unit}`;

    // For generation with curtailment data, show adjusted label
    const effectiveLabel =
      metric.actualCurtailmentLoss && metric.actualCurtailmentLoss > 0
        ? `${label} + estimated curtailed`
        : label;

    let comparison: string;
    if (isDemand) {
      comparison = `${formatPct(metric.pctVsP50)} forecasted`;
    } else if (metric.pctVsP50 === null && metric.pctVsP90 === null) {
      comparison = "0 forecasted";
    } else {
      comparison = `${formatPct(metric.pctVsP50)} P50, ${formatPct(metric.pctVsP90)} P90`;
    }

    const curtailmentNote =
      metric.actualCurtailmentLoss && metric.actualCurtailmentLoss > 0
        ? `Of which, estimated curtailed: ${formatActual(metric.actualCurtailmentLoss)} kWh`
        : null;

    return (
      <>
        <div style={{ color, fontWeight }}>
          {icon} {effectiveLabel}: {valueDisplay} ({comparison})
        </div>
        {curtailmentNote && (
          <div
            style={{
              color: "#666",
              fontWeight: "normal",
              marginLeft: "1.6em",
              fontSize: "0.95em",
            }}
          >
            {curtailmentNote}
          </div>
        )}
      </>
    );
  };

  const renderForecastComparison = () => {
    if (!forecastComparison) return null;

    if (
      monthInfo?.isFirstMonth &&
      monthInfo?.isPartialMonth &&
      monthInfo.operationalDays !== null &&
      monthInfo.operationalDays < 10
    ) {
      return (
        <div
          style={{
            fontSize: "0.85em",
            color: "#666",
            fontStyle: "italic",
            marginTop: "4px",
          }}
        >
          Site only operated for {monthInfo.operationalDays} day
          {monthInfo.operationalDays !== 1 ? "s" : ""} — forecast comparison
          skipped
        </div>
      );
    }

    return (
      <div style={{ fontSize: "0.85em", marginTop: "4px" }}>
        {renderForecastLine("Generation", forecastComparison.generation)}
        {renderForecastLine("Demand", forecastComparison.demand, {
          isDemand: true,
        })}
        {renderForecastLine(
          hasBattery ? "Behind Meter PV" : "Behind Meter",
          forecastComparison.behindMeterPV
        )}
        {hasBattery &&
          renderForecastLine(
            "Behind Meter Battery",
            forecastComparison.behindMeterBattery
          )}
        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          {renderForecastLine("Export", forecastComparison.export)}
          {forecastComparison.export?.p50 != null && (
            <Tooltip
              position="right"
              maxWidth="350px"
              text={`For exports we also check that they are not unexpectedly high as this would risk us overdelivering on our export PPA.\n\nWarn > ${formatActual(forecastComparison.export.p50 + Math.max(0.1 * forecastComparison.export.p50, 1_000))} kWh (P50 + max(10%, 1,000))\nAlert > ${formatActual(forecastComparison.export.p50 + Math.max(0.25 * forecastComparison.export.p50, 10_000))} kWh (P50 + max(25%, 10,000))`}
            />
          )}
        </div>
        {renderForecastLine(
          `Customer revenue`,
          forecastComparison.customerRevenue,
          { unit: "£" }
        )}
      </div>
    );
  };

  // Zero export check rendering
  const renderZeroExportCheck = () => {
    if (!zeroExportCheck || !zeroExportCheck.isZeroExportSite) return null;

    if (zeroExportCheck.hasIssue) {
      const periods = zeroExportCheck.breachingPeriods ?? [];
      return (
        <div style={{ fontSize: "0.85em", marginTop: "4px" }}>
          <div
            style={{
              color: "#dc3545",
              fontWeight: "bold",
            }}
          >
            {"\u26A0"} Zero-export site with significant per-period export
            detected: max {zeroExportCheck.maxPeriodGridExport?.toFixed(3)} kWh
            in a single period (threshold:{" "}
            {zeroExportCheck.thresholdKwh?.toFixed(3)} kWh)
            {zeroExportCheck.totalBreachingBlocks != null &&
              zeroExportCheck.totalBreachingBlocks > periods.length &&
              ` - showing top ${periods.length} periods with export out of ${zeroExportCheck.totalBreachingBlocks} total periods`}
          </div>
          {periods.map((period, idx) => (
            <div
              key={idx}
              style={{
                fontSize: "0.8em",
                color: "#666",
                marginLeft: "20px",
              }}
            >
              {formatIssueTime(period.start, period.end, siteTimeZone)} — max:{" "}
              {period.maxExport.toFixed(3)} kWh
            </div>
          ))}
        </div>
      );
    }

    return (
      <div
        style={{
          fontSize: "0.85em",
          color: "#666",
          marginTop: "4px",
        }}
      >
        Zero-export check: OK (max period export:{" "}
        {zeroExportCheck.maxPeriodGridExport?.toFixed(3)} kWh, threshold:{" "}
        {zeroExportCheck.thresholdKwh?.toFixed(3)} kWh)
      </div>
    );
  };

  const renderPeriodChecks = () => {
    if (!periodChecks) return null;

    const issues = periodChecks.issues;
    const spikesSummary = periodChecks.demandSpikeSummary;
    const demandZeroSummary = periodChecks.demandDropToZeroSummary;
    const genZeroSummary = periodChecks.generationDropToZeroSummary;

    const hasAnyIssues = issues.length > 0;
    const isFirstMonth = monthInfo?.isFirstMonth ?? false;

    if (!hasAnyIssues && !isFirstMonth) return null;

    const renderIssueBlocks = (type: PeriodIssue["type"]) => {
      const typeIssues = issues.filter((i) => i.type === type);
      return typeIssues.map((issue, idx) => (
        <div
          key={idx}
          style={{
            fontSize: "0.8em",
            color: "#666",
            marginLeft: "20px",
          }}
        >
          {issue.esysLabel && `${issue.esysLabel}: `}
          {formatIssueTime(issue.start, issue.end, siteTimeZone)}
          {issue.maxValue !== undefined &&
            ` — max: ${formatActual(issue.maxValue)} kWh`}
        </div>
      ));
    };

    const spikeIssues = issues.filter((i) => i.type === "demandSpike");
    const redSpikes = spikeIssues.filter((i) => i.severity === "red");
    const amberSpikes = spikeIssues.filter((i) => i.severity !== "red");
    const demandZeroIssues = issues.filter(
      (i) => i.type === "demandDropToZero"
    );
    const genZeroIssues = issues.filter(
      (i) => i.type === "generationDropToZero"
    );

    const renderSpikeBlocks = (spikes: PeriodIssue[]) =>
      spikes.map((issue, idx) => (
        <div
          key={idx}
          style={{
            fontSize: "0.8em",
            color: "#666",
            marginLeft: "20px",
          }}
        >
          {formatIssueTime(issue.start, issue.end, siteTimeZone)}
          {issue.maxValue !== undefined &&
            ` — max: ${formatActual(issue.maxValue)} kWh (${((issue.maxValue / spikesSummary.p95Demand) * 100).toFixed(1)}% of p95)`}
        </div>
      ));

    return (
      <div style={{ fontSize: "0.85em", marginTop: "4px" }}>
        {redSpikes.length > 0 && (
          <>
            <div style={{ color: "#dc3545", fontWeight: "bold" }}>
              {"\uD83D\uDD34"} Demand spike with adjacent drop (likely data
              issue): {redSpikes.length} occurrence
              {redSpikes.length !== 1 ? "s" : ""}
            </div>
            {renderSpikeBlocks(redSpikes)}
          </>
        )}
        {spikesSummary.hasIssue && amberSpikes.length > 0 && (
          <>
            <div style={{ color: "#b8860b", fontWeight: "bold" }}>
              {"\uD83D\uDFE0"} Demand spikes: {amberSpikes.length} occurrence
              {amberSpikes.length !== 1 ? "s" : ""} above 130% of p95 demand
            </div>
            {renderSpikeBlocks(amberSpikes)}
          </>
        )}
        {demandZeroIssues.length > 0 && (
          <>
            <div style={{ color: "#dc3545", fontWeight: "bold" }}>
              {"\uD83D\uDD34"} Demand dropped to zero: {demandZeroIssues.length}{" "}
              occurrence
              {demandZeroIssues.length !== 1 ? "s" : ""}
            </div>
            {renderIssueBlocks("demandDropToZero")}
          </>
        )}
        {genZeroIssues.length > 0 && (
          <>
            <div style={{ color: "#dc3545", fontWeight: "bold" }}>
              {"\uD83D\uDD34"} Generation dropped to zero during expected
              generation: {genZeroIssues.length} occurrence
              {genZeroIssues.length !== 1 ? "s" : ""}
            </div>
            {renderIssueBlocks("generationDropToZero")}
          </>
        )}
        {isFirstMonth &&
          (demandZeroSummary.skipped || genZeroSummary.skipped) && (
            <div
              style={{ color: "#666", fontStyle: "italic", marginTop: "2px" }}
            >
              Demand/generation zero checks skipped (first month of actuals)
            </div>
          )}
      </div>
    );
  };

  const hasFileDbComparison = fileReading && !fileReading.error;
  const hasForecastOrZeroExport =
    forecastComparison || (zeroExportCheck && zeroExportCheck.isZeroExportSite);
  const hasPeriodIssues =
    (periodChecks && periodChecks.issues.length > 0) || monthInfo?.isFirstMonth;

  const sectionHeader = (text: string) => (
    <div
      style={{
        fontSize: "0.85em",
        fontWeight: "bold",
        color: "#444",
        marginTop: "4px",
      }}
    >
      {text}
    </div>
  );

  return (
    <>
      {hasFileDbComparison && sectionHeader("Data")}
      {renderFileDbComparison()}
      {hasForecastOrZeroExport && sectionHeader("Forecast check")}
      {renderForecastComparison()}
      {renderZeroExportCheck()}
      {hasPeriodIssues && sectionHeader("Monthly analysis checks")}
      {renderPeriodChecks()}
    </>
  );
};

export default ReadingValidation;
